import { Box, Divider, IconButton, Skeleton, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import { Button, Icon } from '@verantos-dev/shared-components';

import { AnnotationConcepts } from './AnnotationConcepts';
import { FinalizedAnnotationModal } from './FinalizedAnnotationModal';
import { MarkNotPresentModal } from './MarkNotPresentModal';
import { useAnnotationConfig } from './useAnnotationConfig';
import { EDITABLE_TASK_STATUSES, STATUS_PENDING } from '../../constants';

export const AnnotationConfig = ({ rightWidth }: { rightWidth: number }) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const {
    annotationStarted,
    startAnnotation,
    selectedNoteId,
    closeAnnotation,
    handleSetAreAllConceptsExpanded,
    annotationWorkspaceData,
    isAnnotationWorkspaceLoading,
    namesOfConceptsWithoutAnnotationsArray,
    conceptsWithoutAnnotations,
    showMarkRemainingNotPresentModal,
    setShowMarkRemainingNotPresentModal,
    showFinalizedAnnotationModal,
    setShowFinalizedAnnotationModal,
    dirtyConceptNames,
    isAnnotationMutating,
    hasAnnotationsInEditMode,
    hasUnsavedNewAnnotations,
  } = useAnnotationConfig();

  if (!selectedNoteId) {
    return null;
  }
  return (
    <Box sx={{ width: rightWidth }}>
      {!annotationStarted ? (
        <Box
          sx={{
            display: 'flex',
            gap: theme.spacing(4),
            justifyContent: 'flex-end',
            alignItems: 'center',
            mt: theme.spacing(10),
            px: theme.spacing(6),
          }}
        >
          <Button
            id="annotation-start-annotation-button"
            displayLabel={
              annotationWorkspaceData?.task.status.toLocaleLowerCase() === STATUS_PENDING
                ? formatMessage({ id: 'annotationContent.noteSection.startAnnotationButton' })
                : formatMessage({ id: 'annotationContent.noteSection.editAnnotationButton' })
            }
            variant="contained"
            sx={{
              backgroundColor: theme.palette.primary.main,
              fontSize: '15px',
              textTransform: 'none',
            }}
            disabled={selectedNoteId == null}
            onClickHandler={startAnnotation}
          />
        </Box>
      ) : (
        <Box
          id="header-cards-and-footer"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 48px)', // Account for container padding (24px top + 24px bottom)
            maxHeight: 'calc(100vh - 48px)',
            borderLeft: `1px solid ${theme.palette.divider}`,
            p: theme.spacing(4),
          }}
        >
          <Box
            id="header"
            sx={{
              flex: '0 0 auto',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
                  <Typography variant="h5">
                    {formatMessage({ id: 'workspaceCommon.annotationConfig.title' })}
                  </Typography>
                </Box>
                <IconButton
                  id="annotation-config-close-icon-button"
                  onClick={() => {
                    closeAnnotation();
                  }}
                >
                  <Icon id="annotation-config-close-icon" icon="clear" width={16} />
                </IconButton>
              </Box>
              <Divider />
              <Box sx={{ display: 'flex', gap: theme.spacing(4), cursor: 'pointer' }}>
                <Typography
                  onClick={() => {
                    handleSetAreAllConceptsExpanded(true);
                  }}
                  variant="body2"
                  sx={{ color: theme.palette.primary.main }}
                >
                  {formatMessage({ id: 'workspaceCommon.annotations.expandAllButton' })}
                </Typography>
                <Typography
                  onClick={() => {
                    handleSetAreAllConceptsExpanded(false);
                  }}
                  variant="body2"
                  sx={{ color: theme.palette.primary.main }}
                >
                  {formatMessage({ id: 'workspaceCommon.annotations.collapseAllButton' })}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              flex: '1 1 auto',
              overflowY: 'auto',
              minHeight: 0, // Important for flexbox scrolling
            }}
          >
            {isAnnotationWorkspaceLoading ? (
              <Box>
                <Skeleton variant="text" width="100%" height="100px" />
              </Box>
            ) : (
              <AnnotationConcepts
                concepts={annotationWorkspaceData?.concepts ?? []}
                annotations={annotationWorkspaceData?.annotations ?? []}
              />
            )}
          </Box>
          <Box
            id="annotation-config-footer-buttons"
            sx={{
              flex: '0 0 auto',
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(2),
              py: theme.spacing(4),
              px: theme.spacing(2),
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Button
              id="mark-remaining-not-present-button"
              displayLabel={formatMessage(
                { id: `annotationContent.annotationConfig.markRemainingNotPresentButton` },
                { conceptCount: namesOfConceptsWithoutAnnotationsArray.length }
              )}
              variant="outlined"
              sx={{
                fontSize: '15px',
                textTransform: 'none',
              }}
              disabled={namesOfConceptsWithoutAnnotationsArray.length === 0 || isAnnotationMutating}
              onClickHandler={() => setShowMarkRemainingNotPresentModal(true)}
            />
            <Button
              id="finalise-encounter-button"
              displayLabel={formatMessage({
                id: 'annotationContent.annotationConfig.finaliseEncounterButton',
              })}
              variant="contained"
              sx={{
                backgroundColor: theme.palette.primary.dark,
                fontSize: '15px',
                textTransform: 'none',
              }}
              disabled={
                namesOfConceptsWithoutAnnotationsArray.length > 0 ||
                !EDITABLE_TASK_STATUSES.includes(
                  annotationWorkspaceData?.task.status.toLowerCase() ?? ''
                ) ||
                isAnnotationMutating ||
                hasAnnotationsInEditMode ||
                hasUnsavedNewAnnotations
              }
              onClickHandler={() => setShowFinalizedAnnotationModal(true)}
            />
          </Box>
        </Box>
      )}

      <MarkNotPresentModal
        showMarkRemainingNotPresentModal={showMarkRemainingNotPresentModal}
        setShowMarkRemainingNotPresentModal={setShowMarkRemainingNotPresentModal}
        namesOfConceptsWithoutAnnotationsArray={namesOfConceptsWithoutAnnotationsArray}
        conceptsWithoutAnnotations={conceptsWithoutAnnotations}
        dirtyConceptNames={dirtyConceptNames}
      />
      <FinalizedAnnotationModal
        showFinalizedAnnotationModal={showFinalizedAnnotationModal}
        setShowFinalizedAnnotationModal={setShowFinalizedAnnotationModal}
      />
    </Box>
  );
};
